import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../features/auth/context';
import { useDocumentTitle } from '../shared/useDocumentTitle';
import { analytics } from '../shared/analytics';
import NameDialog from '../components/NameDialog';
import TripDetailNav from '../features/my-trips/components/TripDetailNav';
import StatusPill from '../features/my-trips/components/StatusPill';
import MetaLine from '../features/my-trips/components/MetaLine';
import AlternativesView from '../features/my-trips/components/AlternativesView';
import { RouteFocusMap } from '../features/my-trips/components/RouteFocus';
import DetailSplitLayout, { useDetailSplit } from '../features/my-trips/components/DetailSplitLayout';
import ApproxNote from '../features/my-trips/components/ApproxNote';
import { resolveDisplayStatus } from '../features/my-trips/utils';
import {
  SharedTripPayload,
  SharedAccessProblem,
  fetchSharedTrip,
  classifyShareError,
  storeSharePassword,
  saveSharedCopy,
} from '../features/share/api';
import {
  useNoIndex,
  useOwnerRedirect,
  SharedNavActions,
  SharedTripStrip,
  SharePasswordGate,
  ShareNotFound,
  ShareExpired,
  ShareTooManyAttempts,
  ShareLoadError,
  ShareWallDialog,
} from '../features/share/visitor';
import SharedShareDialog from '../features/share/SharedShareDialog';

/**
 * Trip sharing v1 — the public, read-only trip view behind /s/<token>
 * (anonymous access v1 §7, trip-level). Reuses the Trip detail building
 * blocks (the desktop split with the map on the right, AlternativesView,
 * RouteFocusMap) with every owner action routed to the ShareWallDialog
 * instead: edit affordances stay visible, tapping one walls.
 * "Save a copy" (signed-in) copies the focused route into the visitor's own
 * account — the conversion action of the share loop.
 */
const SharedTripPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Guests (device token, no JWT) count as signed OUT here: saving a copy
  // needs a real account.
  const { user, loading: authLoading } = useAuth();
  const signedIn = !!user;

  useNoIndex();
  // Owner on their own link → their trip page; the shared view waits for it.
  const ownerChecked = useOwnerRedirect(token);

  const [payload, setPayload] = useState<SharedTripPayload | null>(null);
  const [problem, setProblem] = useState<SharedAccessProblem | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverPreviewId, setHoverPreviewId] = useState<string | null>(null);
  const [wallOpen, setWallOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [savingCopy, setSavingCopy] = useState(false);
  const viewTrackedRef = useRef(false);

  useDocumentTitle(payload ? payload.share.tripTitle : 'Shared trip');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await fetchSharedTrip(token);
      setPayload(data);
      setProblem(null);
      // ?focus= picks the first route shown: it rides along when an app URL
      // (/trips/<id>?focus=…) is redirected here.
      const focus = new URLSearchParams(window.location.search).get('focus');
      setSelectedId((prev) => {
        const ids = data.trip.alternatives.map((a) => a.id);
        if (prev && ids.includes(prev)) return prev;
        if (focus && ids.includes(focus)) return focus;
        return data.trip.mainJourneyId && ids.includes(data.trip.mainJourneyId)
          ? data.trip.mainJourneyId
          : data.trip.alternatives[0]?.id ?? null;
      });
    } catch (e) {
      setProblem(classifyShareError(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (ownerChecked) load(); }, [ownerChecked, load]);

  // Fires once the payload AND auth have both resolved (whichever finishes
  // last), so `signed_in` is never read while useAuth is still loading — a
  // signed-in visitor's fast payload fetch used to beat auth resolution and
  // get permanently mis-logged as anonymous (ultrareview finding,
  // 2026-09-14; viewTrackedRef stays a one-shot latch, now here).
  useEffect(() => {
    if (!payload || authLoading || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    analytics.trackTripShareLinkViewed({
      trip_id: payload.trip.id,
      visibility: payload.share.visibility,
      signed_in: signedIn,
      surface: 'trip',
    });
  }, [payload, authLoading, signedIn]);

  const selected = useMemo(
    () => payload?.trip.alternatives.find((a) => a.id === selectedId) ?? null,
    [payload, selectedId],
  );
  const hoverPreview = useMemo(
    () => payload?.trip.alternatives.find((a) => a.id === hoverPreviewId) ?? null,
    [payload, hoverPreviewId],
  );

  const openWall = useCallback(() => setWallOpen(true), []);
  const openRoute = useCallback((jid: string) => {
    if (token) navigate(`/s/${encodeURIComponent(token)}/journeys/${encodeURIComponent(jid)}`);
  }, [token, navigate]);
  // Desktop: the map on the right, the routes stacked on the left (as on the
  // owner's Trip detail).
  const split = useDetailSplit();

  // Save a copy = the WHOLE trip (all alternatives). Same contract as the
  // owner's Create copy (Paulo 2026-09-14): the shared NameDialog first,
  // prefilled "Copy of …"; `focusJourneyId` only picks which alternative the
  // visitor lands on in their new trip.
  const [copyPrompt, setCopyPrompt] = useState<null | { focusJourneyId?: string }>(null);
  const requestSaveCopy = useCallback((focusJourneyId?: string) => {
    if (!signedIn) { setWallOpen(true); return; }
    setCopyPrompt({ focusJourneyId });
  }, [signedIn]);
  const handleSaveCopy = useCallback(async (title: string) => {
    if (!token || !copyPrompt) return;
    setSavingCopy(true);
    try {
      const created = await saveSharedCopy(token, title);
      const { focusJourneyId } = copyPrompt;
      const focus = (focusJourneyId && created.journeyIds[focusJourneyId]) || created.mainJourneyId;
      showToast({ title: 'Saved', message: `"${title}" and all its routes are now in My Trips.`, type: 'success' });
      navigate(`/trips/${encodeURIComponent(created.tripId)}${focus ? `?focus=${encodeURIComponent(focus)}` : ''}`);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not save a copy. Please try again.', type: 'error' });
    } finally {
      setSavingCopy(false);
    }
  }, [token, copyPrompt, navigate, showToast]);

  const handlePassword = useCallback((password: string) => {
    if (!token) return;
    storeSharePassword(token, password);
    load();
  }, [token, load]);

  // ── Terminal / gate states ──────────────────────────────────────────────
  if (!token || problem === 'not_found') return <ShareNotFound />;
  if (problem === 'expired') return <ShareExpired />;
  if (problem === 'too_many_attempts') return <ShareTooManyAttempts />;
  if (problem === 'network') return <ShareLoadError onRetry={load} />;
  if (problem === 'password_required' || problem === 'password_incorrect') {
    return (
      <SharePasswordGate
        error={problem === 'password_incorrect'}
        busy={loading}
        onSubmit={handlePassword}
      />
    );
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <DetailSplitLayout
        map={split && !loading && selected ? (
          <RouteFocusMap alt={selected} previewAlt={hoverPreview} onSeeDetails={openRoute} fill />
        ) : null}
      >
        {loading || !payload ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading trip…</div>
        ) : (
          <>
            <TripDetailNav
              title={payload.share.tripTitle}
              onBack={() => navigate('/')}
              actions={(
                <SharedNavActions
                  onShare={() => setShareOpen(true)}
                  onSaveCopy={() => requestSaveCopy()}
                  savingCopy={savingCopy}
                />
              )}
            />

            <SharedTripStrip ownerFirstName={payload.share.ownerFirstName} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <StatusPill status={resolveDisplayStatus(payload.trip.dateStart, payload.trip.dateEnd, payload.trip.stage)} />
              <MetaLine
                dateStart={payload.trip.dateStart}
                dateEnd={payload.trip.dateEnd}
                alternativesCount={payload.trip.alternativesCount}
                durationPreset={payload.trip.durationPreset}
              />
            </div>

            {payload.trip.alternatives.length === 0 ? (
              <div style={{ color: '#6c7872', padding: '12px 0' }}>
                This trip has no routes yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#16241d', margin: 0 }}>Route alternatives</h2>
                <AlternativesView
                  alternatives={payload.trip.alternatives}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  hoverPreviewId={hoverPreviewId}
                  onHoverChange={setHoverPreviewId}
                  onOpen={openRoute}
                  // Visitor kebab = relevant rows only (UX decision, Paulo
                  // 2026-09-14): Details + Save a copy. Owner management rows
                  // (rename/delete/main) would wall on something signing up
                  // can't unlock, so they read as bugs, not teasers. The
                  // desirability probe lives on the CONTENT edit affordances.
                  menu={{ onCopy: (jid) => requestSaveCopy(jid), copyLabel: 'Save a copy' }}
                  onSetMain={openWall}
                  invitePricing={false}
                  split={split}
                />
                <ApproxNote />
              </div>
            )}
          </>
        )}
      </DetailSplitLayout>

      {shareOpen && payload && token && (
        <SharedShareDialog
          token={token}
          tripId={payload.trip.id}
          tripTitle={payload.share.tripTitle}
          source="shared_trip"
          onClose={() => setShareOpen(false)}
        />
      )}

      {copyPrompt && payload && (
        <NameDialog
          title="Save a copy"
          label="Name for the trip copy"
          confirmText="Save copy"
          initialValue={`Copy of ${payload.share.tripTitle}`}
          busy={savingCopy}
          onConfirm={handleSaveCopy}
          onCancel={() => { if (!savingCopy) setCopyPrompt(null); }}
        />
      )}

      {wallOpen && payload && token && (
        <ShareWallDialog
          token={token}
          signedIn={signedIn}
          ownerFirstName={payload.share.ownerFirstName}
          savingCopy={savingCopy}
          onSaveCopy={() => { setWallOpen(false); requestSaveCopy(); }}
          onClose={() => setWallOpen(false)}
        />
      )}
    </div>
  );
};

export default SharedTripPage;
